import { useQuery } from '@tanstack/react-query'
import { useBatchPricing } from '~/client/hooks/useBatchPricing'
import { QUERY_KEY, queryPricing } from '~/client/queries/queryPricing'
import { PricingParameters, TemplatePricingProps } from '~/client/components/Gallery/DesignTile/interface'
import { booleanRenderPropertySelector, getLocaleSelector } from '~/client/store/config'
import { useSelector } from 'react-redux'
import { getPricingPresentationType } from '~/client/utils/getPricingPresentationType'
import { RenderProperty } from '~/shared/renderProperties'
import { usePricingContext } from 'client/contexts/pricing/context'

export const usePricing = ({
  tileEntity,
  differentialPrice,
  pricingPresentationType,
  market,
  quantity,
  hideVatMessage,
  hideShippingMessageInVatMessage,
  productOptionsByProductKey = null,
}: TemplatePricingProps): PricingParameters => {
  const locale = useSelector(getLocaleSelector)
  const pricingContext = usePricingContext()
  const retrievedPricingContext = pricingContext?.getPricingContext()
  const booleanRenderProperty = useSelector(booleanRenderPropertySelector)

  const { productKey, productVersion, fullProductOptions, productOptionsHash, comparativeName } = tileEntity
  const showComparativeNames = booleanRenderProperty(RenderProperty.ShowComparativeNames)
  const shouldShowComparativeNames = showComparativeNames && !!comparativeName

  const templateTileTitle = comparativeName || ''
  const resolvedPricingPresentation =
    differentialPrice && pricingPresentationType ? getPricingPresentationType(pricingPresentationType) : pricingPresentationType

  const { differentialPriceData, productQuantity } = useBatchPricing({
    productOptionsByProductKey,
    productKey,
    productVersion,
    differentialPrice,
    fullProductOptions,
    locale,
    market,
    quantity,
    pricingContext,
    retrievedPricingContext,
  })

  const enabled = !!differentialPriceData && !!productKey && !!productVersion && !!productQuantity

  const {
    data: formattedPrices,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      QUERY_KEY,
      {
        pricingResponse: differentialPriceData || undefined,
        quantity: productQuantity,
        locale,
        vatInc: retrievedPricingContext?.vatInclusive || false,
        hideVatMessage: hideVatMessage || differentialPrice,
        pricingPresentationType: resolvedPricingPresentation || '',
        hideShippingMessageInVatMessage: hideShippingMessageInVatMessage || false,
        productOptionsHash,
      },
    ],
    queryFn: queryPricing,
    enabled,
  })

  return {
    templateTileTitle,
    shouldShowComparativeNames,
    formattedPrices,
    isLoading,
    isError,
  }
}
